/**
 * What the signed-in role may do on screen. This mirrors the rules the API enforces
 * (api/modules/access.py): the screens hide what would be refused, the API still decides.
 *
 *   admin    the chart of accounts, settings                      Admin
 *   master   employees, departments, positions, schedules, ...     Admin, Manager
 *   close    closing or reopening a period                         Admin, Manager
 *   period   the rows of a pay period: payslips, transactions,     Admin, Manager; Supervisor and
 *            daily times, loan payments                            Staff while the period is Open
 *   operate  leave credits, loans                                  Admin, Manager, Supervisor
 */
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'

export type Capability = 'admin' | 'master' | 'close' | 'period' | 'operate'

/** Pure rule, so it is easy to test: roles held, the capability, and the period's status. */
export function allowed(roles: string[], what: Capability, periodStatus?: string): boolean {
  const has = (role: string) => roles.includes(role)
  if (what === 'admin') return has('Admin')
  if (has('Admin') || has('Manager')) return true
  if (what === 'operate') return has('Supervisor')
  if (what === 'period') return (has('Supervisor') || has('Staff')) && periodStatus === 'Open'
  return false // master, close
}

export function usePermissions() {
  const auth = useAuthStore()
  const app = useAppStore()
  const can = (what: Capability) => allowed(auth.roles, what, app.thisPeriod?.status)
  const isManagement = computed(() => auth.isManagement)
  return { can, isManagement }
}
